// The contact book.
// Route: /settings/contacts

import { useEffect, useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Confirm,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  Pager,
  RelativeTime,
  Search,
  Spinner,
  Table,
  Tag,
  useAsync,
  useToast,
  type Column,
} from "../ui";
import {
  PAGE_SIZE,
  errMessage,
  ErrorBanner,
  useDebounced,
  hasAction,
  useAccounts,
} from "./shared";
import { TagChip, type TagListPayload, type TagRow } from "./tags";
import { ImportExportDialog } from "./import_export";

// ============================================================ contacts

interface Contact {
  id: string;
  phone_number: string;
  name: string;
  profile_name: string;
  tags: string[];
  whatsapp_account?: string;
  assigned_user_id?: string | null;
  last_message_at?: string | null;
  last_message_preview: string;
  unread_count: number;
  created_at: string;
  updated_at: string;
}

interface ContactListPayload {
  contacts?: Contact[];
  total?: number;
}

interface ContactForm {
  phone_number: string;
  profile_name: string;
  whatsapp_account: string;
  tags: string[];
}

const EMPTY_CONTACT_FORM: ContactForm = {
  phone_number: "",
  profile_name: "",
  whatsapp_account: "",
  tags: [],
};

function displayName(contact: Contact): string {
  return contact.profile_name || contact.name || contact.phone_number;
}

/** The tag picker shared by the create and edit dialogs. */
function TagPicker({
  available,
  selected,
  onChange,
}: {
  available: TagRow[];
  selected: string[];
  onChange: (tags: string[]) => void;
}) {
  if (!available.length) return null;
  const toggle = (name: string) =>
    onChange(selected.includes(name) ? selected.filter((t) => t !== name) : [...selected, name]);

  return (
    <Field label="Tags">
      <div className="col">
        <div
          className="chips"
          style={{ maxHeight: 132, overflowY: "auto", padding: 4, border: "1px solid var(--border-2)", borderRadius: "var(--r-sm)" }}
        >
          {available.map((tag) => (
            <label className="check" key={tag.name}>
              <input
                type="checkbox"
                checked={selected.includes(tag.name)}
                onChange={() => toggle(tag.name)}
              />
              <TagChip name={tag.name} color={tag.color} />
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="chips">
            {selected.map((name) => (
              <Tag key={name} label={name} onRemove={() => toggle(name)} />
            ))}
          </div>
        )}
      </div>
    </Field>
  );
}

export function ContactsView() {
  const { me, can } = useSession();
  const toast = useToast();
  const canWrite = can("contacts", "write");
  const canDelete = can("contacts", "delete");
  const canImport = hasAction(me, "contacts", "import");
  const canExport = hasAction(me, "contacts", "export");

  const [search, setSearch] = useState("");
  const query = useDebounced(search);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const { data, error, loading, reload } = useAsync<ContactListPayload>(
    () => http.get<ContactListPayload>(`/contacts${qs({ search: query, page, limit: PAGE_SIZE })}`),
    [query, page]
  );
  const contacts = data?.contacts ?? [];
  const total = data?.total ?? contacts.length;

  const accounts = useAccounts();
  const [tags, setTags] = useState<TagRow[]>([]);
  useEffect(() => {
    void http
      .get<TagListPayload>(`/tags${qs({ limit: 100 })}`)
      // Silent: the tag picker is optional, the rest of the screen is not.
      .then((res) => setTags(res.tags ?? []))
      .catch(() => setTags([]));
  }, []);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [form, setForm] = useState<ContactForm>(EMPTY_CONTACT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [toDelete, setToDelete] = useState<Contact | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);

  const openCreate = () => {
    setForm(EMPTY_CONTACT_FORM);
    setCreateOpen(true);
  };

  const openEdit = (contact: Contact) => {
    setEditing(contact);
    setForm({
      phone_number: contact.phone_number,
      profile_name: contact.profile_name || "",
      whatsapp_account: contact.whatsapp_account || "",
      tags: contact.tags ?? [],
    });
  };

  const resetDisclaimer = async (contact: Contact) => {
    // Clears the contact-level timestamps AND the per-session flags. The backend does
    // both deliberately: the timestamps drive the rolling 24h re-prompt, the session
    // flags are what an already-running conversation checks, and clearing only one
    // leaves the disclaimer suppressed until the session ends.
    try {
      await http.post(`/contacts/${contact.id}/reset-disclaimer`, {});
      toast.push("The disclaimer will show again on the next inbound message", "good");
    } catch (err) {
      toast.push(errMessage(err, "Could not reset the disclaimer"), "critical");
    }
  };

  const create = async () => {
    if (!form.phone_number.trim()) {
      toast.push("A phone number is required", "critical");
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/contacts", {
        phone_number: form.phone_number.trim(),
        profile_name: form.profile_name || undefined,
        whatsapp_account: form.whatsapp_account || undefined,
        tags: form.tags.length ? form.tags : undefined,
      });
      toast.push("Contact created", "good");
      setCreateOpen(false);
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not create the contact"), "critical");
    } finally {
      setSubmitting(false);
    }
  };

  const update = async () => {
    if (!editing) return;
    setSubmitting(true);
    try {
      await http.put(`/contacts/${editing.id}`, {
        profile_name: form.profile_name,
        whatsapp_account: form.whatsapp_account,
        tags: form.tags,
      });
      toast.push("Contact updated", "good");
      setEditing(null);
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not save the contact"), "critical");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await http.del(`/contacts/${toDelete.id}`);
      toast.push("Contact deleted", "good");
      setToDelete(null);
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not delete the contact"), "critical");
      throw err;
    }
  };

  const columns: Column<Contact>[] = [
    {
      key: "profile_name",
      header: "Name",
      sortable: true,
      render: (c) => (
        <div>
          <div className="strong">{displayName(c)}</div>
          {c.last_message_preview && (
            <div className="muted truncate" style={{ maxWidth: 220 }}>
              {c.last_message_preview}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "phone_number",
      header: "Phone number",
      sortable: true,
      render: (c) => <code className="mono">{c.phone_number}</code>,
    },
    {
      key: "tags",
      header: "Tags",
      render: (c) => {
        const list = c.tags ?? [];
        return (
          <div className="chips">
            {list.slice(0, 3).map((name) => (
              <TagChip key={name} name={name} color={tags.find((t) => t.name === name)?.color} />
            ))}
            {list.length > 3 && <span className="tag">+{list.length - 3}</span>}
          </div>
        );
      },
    },
    {
      key: "last_message_at",
      header: "Last message",
      sortable: true,
      render: (c) =>
        c.last_message_at ? <RelativeTime value={c.last_message_at} /> : <span className="muted">Never</span>,
    },
    {
      key: "created_at",
      header: "Created",
      sortable: true,
      render: (c) => <RelativeTime value={c.created_at} />,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (c) => (
        <div className="row" style={{ justifyContent: "flex-end" }}>
          {/* The disclaimer gate is a committed requirement: no guidance is emitted
              before the disclosure is accepted. Its only operational control used to
              live on the conversation screen, which came out of scope, and the backend
              endpoint was left serving nobody. It belongs here now, because the contact
              is what the gate is keyed on. The row previously linked to /chat/{id},
              which no longer exists. */}
          <button
            className="btn icon sm ghost"
            title="Show the disclaimer again on this contact's next message"
            disabled={!canWrite}
            onClick={() => void resetDisclaimer(c)}
          >
            ⚖
          </button>
          <button
            className="btn icon sm ghost"
            title="Edit"
            disabled={!canWrite}
            onClick={() => openEdit(c)}
          >
            ✎
          </button>
          <button
            className="btn icon sm ghost"
            title="Delete"
            disabled={!canDelete}
            onClick={() => setToDelete(c)}
          >
            ✕
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Contacts</h1>
          <div className="sub">
            <a href="/settings">Settings</a> · everyone this organisation has spoken to.
          </div>
        </div>
        <div className="page-actions">
          {(canImport || canExport) && (
            <button className="btn" onClick={() => setTransferOpen(true)}>
              ⭳ Import / export
            </button>
          )}
          {canWrite && (
            <button className="btn primary" onClick={openCreate}>
              + Add contact
            </button>
          )}
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the contacts" />

      <div className="card flush">
        <div className="card-head">
          <h3>All contacts</h3>
          <Search value={search} onChange={setSearch} placeholder="Search name or number…" />
        </div>
        <div className="card-body">
          <Table
            columns={columns}
            rows={contacts}
            loading={loading}
            empty={
              <Empty
                title={query ? "No matching contacts" : "No contacts yet"}
                body={
                  query
                    ? `Nothing matches "${query}".`
                    : "A contact appears here the first time they message you, or you can add one."
                }
                icon="◍"
                action={
                  canWrite ? (
                    <button className="btn primary sm" onClick={openCreate}>
                      + Add contact
                    </button>
                  ) : undefined
                }
              />
            }
          />
        </div>
        <div className="card-foot">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={(page - 1) * PAGE_SIZE}
            itemName="contacts"
            onChange={(offset) => setPage(Math.floor(offset / PAGE_SIZE) + 1)}
          />
        </div>
      </div>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} title="Add contact">
        <Field label="Phone number" hint="Country code included, for example +447700900000." required>
          <input
            value={form.phone_number}
            onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
          />
        </Field>
        <Field label="Name">
          <input
            value={form.profile_name}
            onChange={(e) => setForm({ ...form, profile_name: e.target.value })}
          />
        </Field>
        {accounts.length > 0 && (
          <Field label="WhatsApp account">
            <select
              value={form.whatsapp_account}
              onChange={(e) => setForm({ ...form, whatsapp_account: e.target.value })}
            >
              <option value="">No account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.name}>
                  {a.name}
                  {a.phone_number ? ` (${a.phone_number})` : ""}
                </option>
              ))}
            </select>
          </Field>
        )}
        <TagPicker available={tags} selected={form.tags} onChange={(t) => setForm({ ...form, tags: t })} />
        <DialogFooter>
          <button className="btn" onClick={() => setCreateOpen(false)} disabled={submitting}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void create()} disabled={submitting}>
            {submitting && <Spinner label="Saving" />}
            Create
          </button>
        </DialogFooter>
      </Dialog>

      <Dialog open={!!editing} onClose={() => setEditing(null)} title="Edit contact">
        <Field label="Phone number" hint="A number cannot be changed once the contact exists.">
          <input value={form.phone_number} disabled readOnly />
        </Field>
        <Field label="Name">
          <input
            value={form.profile_name}
            onChange={(e) => setForm({ ...form, profile_name: e.target.value })}
          />
        </Field>
        {accounts.length > 0 && (
          <Field label="WhatsApp account">
            <select
              value={form.whatsapp_account}
              onChange={(e) => setForm({ ...form, whatsapp_account: e.target.value })}
            >
              <option value="">No account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.name}>
                  {a.name}
                  {a.phone_number ? ` (${a.phone_number})` : ""}
                </option>
              ))}
            </select>
          </Field>
        )}
        <TagPicker available={tags} selected={form.tags} onChange={(t) => setForm({ ...form, tags: t })} />
        <DialogFooter>
          <button className="btn" onClick={() => setEditing(null)} disabled={submitting}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void update()} disabled={submitting}>
            {submitting && <Spinner label="Saving" />}
            Update
          </button>
        </DialogFooter>
      </Dialog>

      <ImportExportDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        table="contacts"
        tableLabel="Contacts"
        filters={query ? { search: query } : undefined}
        canImport={canImport}
        canExport={canExport}
        onImported={reload}
      />

      <Confirm
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => void remove()}
        title="Delete this contact?"
        body={`${toDelete ? displayName(toDelete) : ""} will be removed from the contact list. The conversation history is kept.`}
        danger
      />
    </div>
  );
}
